import { Box, Container, Flex, Text, Button, Loader, Center, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import AnonymousProfile from "@/components/shared/AnonymousCompanyProfile/AnonymousProfile";
import MatchedProfileView from "@/pages/profile/MatchedView";
import { IconSend } from "@tabler/icons-react";
import ApplyVerificationModal from "../profile-verification/ApplyVerificationModal";
import VerificationSubmittedModal from "@/pages/profile-verification/VerificationSubmittedModal";
import RejectionDetailsModal from "@/pages/profile-verification/VerificationRejectedModal";
import VerificationRejectedBanner from "../profile-verification/VerificationRejectedBanner";
import { useUserContext } from "@/context/UserContext";

export default function CompanyProfileView() {
  const navigate = useNavigate();
  const { userDetails, hasProfile } = useUserContext();
  const [activeView, setActiveView] = useState<"anonymized" | "matched">("anonymized");
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [verificationModalOpened, setVerificationModalOpened] = useState(false);
  const [verificationSubmittedModalOpened, setVerificationSubmittedModalOpened] = useState(false);
  const [rejectionModalOpened, setRejectionModalOpened] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [verificationData, setVerificationData] = useState<any>(null);

  const fetchCompanyData = useCallback(async () => {
    if (hasProfile === null) return;
    const companyId = userDetails?.company?.id;

    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const companyData = await trpc.company.getById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [hasProfile, userDetails?.company?.id]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const handleApplyForVerification = async () => {
    if (!company?.id) return;

    setIsApplying(true);
    try {
      const result = await trpc.verification.verifyCompany.mutate({
        legalName: company.legalName,
        registrationNumber: company.registrationNumber,
        headquartersCountry: company.headquartersCountry,
        state: company.state ?? undefined,
      });

      if (result?.success && "data" in result && result.data) {
        setVerificationData({
          ...result.data,
          _verificationSource: result.source,
        });
        setVerificationModalOpened(true);
      } else if (result && !result.success && "pending24h" in result && result.pending24h) {
        notifications.show({
          title: "Verification pending",
          message: "Your verification request has been submitted successfully. Please wait up to 24 hours, then try again.",
          color: "blue",
        });
      }

      await fetchCompanyData(); // Refresh the profile to capture SYSTEM_VERIFIED status or UNDER_REVIEW
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCloseVerificationModal = () => {
    setVerificationModalOpened(false);
    setTimeout(() => setVerificationData(null), 200);
  };

  if (loading) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size="xl" py="xl">
        <Text>Company not found. Please create your company profile first.</Text>
        <Button mt="md" onClick={() => navigate("/create-profile")}>
          Go to Dashboard
        </Button>
      </Container>
    );
  }

  const companyStatus = company.status;
  const isRejected = companyStatus === "ACTION_REQUIRED";
  const canApplyForVerification = companyStatus === "DRAFT" || companyStatus === "ACTION_REQUIRED";

  const renderHeader = () => (
    <>
      <Box py="md" style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Flex
          justify="space-between"
          pl={20}
          pr={20}
          align="flex-start"
          direction={{ base: "column", sm: "row" }}
          gap="sm"
        >
          <Title order={2} fw={400} fz={24}>
              Welcome! {company.legalName}
          </Title>
          <Flex
            gap="sm"
            align={{ base: "stretch", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "100%", sm: "auto" }}
          >
            {canApplyForVerification && (
              <Button
                color="#34a853"
                size="sm"
                fz="sm"
                leftSection={<IconSend size={18} />}
                onClick={() => setVerificationModalOpened(true)}
                style={{ whiteSpace: "nowrap" }}
                w={{ base: "100%", sm: "auto" }}
              >
                Apply for Verification
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      <Box py="md">
        <Container size="fluid" px={{ base: "sm", sm: "md" }}>
          <Flex direction="column" gap="sm" w="100%" maw="100%">
            <Flex justify="space-between" align="stretch" wrap="wrap" gap="sm" w="100%">
              <Flex
                direction="column"
                gap="xs"
                w="100%"
                maw={{ base: "100%", md: 1140 }}
                style={{ backgroundColor: "#F3F4F6", padding: "8px", borderRadius: "10px" }}
              >
                <Flex
                  gap="xs"
                  align="stretch"
                  direction={{ base: "column", md: "row" }}
                  wrap="nowrap"
                  w="100%"
                >
                  <Button
                    variant={activeView === "anonymized" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    flex={1}
                    miw={0}
                    w={{ base: "100%", md: "auto" }}
                    maw={{ md: 560 }}
                    onClick={() => setActiveView("anonymized")}
                    styles={{
                      root: {
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        textAlign: "center",
                        justifyContent: "center",
                        backgroundColor:
                          activeView === "anonymized" ? "var(--mantine-color-primary-6)" : "#FFFFFF",
                        color: activeView === "anonymized" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "anonymized"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor:
                            activeView === "anonymized" ? "var(--mantine-color-primary-7)" : "#F9FAFB",
                        },
                      },
                      inner: {
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      },
                      label: {
                        width: "100%",
                        textAlign: "center",
                        whiteSpace: "normal",
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center" justify="center" w="100%" maw="100%" px={4}>
                      <Text fw={500} size="sm" c={activeView === "anonymized" ? "#FFFFFF" : "#1F2937"} ta="center" style={{ width: "100%" }}>
                        Anonymized View
                      </Text>
                      <Text size="xs" c={activeView === "anonymized" ? "rgba(255,255,255,0.92)" : "#6B7280"} ta="center" style={{ width: "100%" }}>
                        How your company profile appears to others before revealing your identity.
                      </Text>
                    </Flex>
                  </Button>
                  <Button
                    variant={activeView === "matched" ? "filled" : "light"}
                    color="gray"
                    size="md"
                    flex={1}
                    miw={0}
                    w={{ base: "100%", md: "auto" }}
                    maw={{ md: 560 }}
                    onClick={() => setActiveView("matched")}
                    styles={{
                      root: {
                        minHeight: "74px",
                        height: "auto",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        textAlign: "center",
                        justifyContent: "center",
                        backgroundColor:
                          activeView === "matched" ? "var(--mantine-color-primary-6)" : "#FFFFFF",
                        color: activeView === "matched" ? "#FFFFFF" : "#1F2937",
                        fontWeight: 500,
                        border: "none",
                        boxShadow:
                          activeView === "matched"
                            ? "0px 1px 3px rgba(37, 99, 235, 0.35), 0px 1px 2px rgba(37, 99, 235, 0.25)"
                            : "none",
                        "&:hover": {
                          backgroundColor:
                            activeView === "matched" ? "var(--mantine-color-primary-7)" : "#F9FAFB",
                        },
                      },
                      inner: {
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      },
                      label: {
                        width: "100%",
                        textAlign: "center",
                        whiteSpace: "normal",
                      },
                    }}
                  >
                    <Flex direction="column" gap={2} align="center" justify="center" w="100%" maw="100%" px={4}>
                      <Text fw={500} size="sm" c={activeView === "matched" ? "#FFFFFF" : "#1F2937"} ta="center" style={{ width: "100%" }}>
                        Matched View
                      </Text>
                      <Text size="xs" c={activeView === "matched" ? "rgba(255,255,255,0.92)" : "#6B7280"} ta="center" style={{ width: "100%" }}>
                        How your full company profile is shared after a mutual match is established.
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Container>
      </Box>

      {isRejected && (
        <Container size="fluid" mb="md">
          <VerificationRejectedBanner onKnowMore={() => setRejectionModalOpened(true)} />
        </Container>
      )}
    </>
  );

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      {renderHeader()}

      {activeView === "anonymized" ? (
        <AnonymousProfile pageID="MY_PROFILE_PREVIEW" companyData={company} />
      ) : (
        <MatchedProfileView company={company} containerSize="fluid" isOwnProfile={true} />
      )}

      <ApplyVerificationModal
        opened={verificationModalOpened}
        onClose={handleCloseVerificationModal}
        onApply={handleApplyForVerification}
        isLoading={isApplying}
        verificationData={verificationData}
      />

      <VerificationSubmittedModal
        opened={verificationSubmittedModalOpened}
        onClose={() => setVerificationSubmittedModalOpened(false)}
      />

      <RejectionDetailsModal
        opened={rejectionModalOpened}
        onClose={() => setRejectionModalOpened(false)}
        adminComments={company?.adminComments}
      />
    </Box>
  );
}
